import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Target, ClipboardList, ShieldCheck, Trophy,
  FileCheck2, Users, LifeBuoy, Settings as SettingsIcon, GraduationCap,
  Bell, LogOut,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/goals", label: "Goals & KPIs", icon: Target },
  { to: "/sessions", label: "Training Sessions", icon: ClipboardList },
  { to: "/readiness", label: "Department Readiness", icon: ShieldCheck },
  { to: "/rankings", label: "Team Rankings", icon: Trophy },
  { to: "/assessments", label: "Assessments", icon: FileCheck2 },
  { to: "/team", label: "Team Members", icon: Users },
  { to: "/low-performers", label: "Coaching & Plans", icon: LifeBuoy },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout() {
  const {
    user, logout, departments, selectedDept, setSelectedDept,
    dateFrom, setDateFrom, dateTo, setDateTo,
  } = useApp();
  const [overdue, setOverdue] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/dashboard", { params: { department_id: selectedDept !== "all" ? selectedDept : undefined } })
      .then((r) => setOverdue(r.data.overdue_members || []))
      .catch(() => {});
  }, [selectedDept, location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#0B1120] relative z-10">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#0B1120] border-r border-slate-800 flex flex-col fixed h-screen">
        <div className="h-20 flex items-center gap-3 px-6 border-b border-slate-800">
          <div className="h-10 w-10 rounded-lg bg-[#C9A24B] flex items-center justify-center shrink-0">
            <GraduationCap className="h-5 w-5 text-[#0B1120]" />
          </div>
          <div className="leading-tight">
            <p className="text-white font-display text-sm">Youngness Centre</p>
            <p className="text-slate-500 text-[10px] tracking-widest uppercase">Training CRM</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#1E293B] text-white border-l-2 border-[#C9A24B]"
                    : "text-slate-400 hover:text-white hover:bg-[#1E293B]/50"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#1E293B] text-[#C9A24B] text-xs">
                {user?.name?.[0] || "T"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs truncate">{user?.name || "Trainer"}</p>
              <p className="text-slate-500 text-[10px] truncate">{user?.email}</p>
            </div>
            <button data-testid="logout-button" onClick={logout} className="text-slate-500 hover:text-[#DC2626]">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-w-0">
        <header className="h-20 sticky top-0 z-20 backdrop-blur-xl bg-[#0F172A]/70 border-b border-white/10 flex items-center gap-4 px-6">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-500">Department</span>
            <Select value={selectedDept} onValueChange={setSelectedDept}>
              <SelectTrigger data-testid="department-select" className="w-[180px] border-slate-700 bg-transparent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <input
              type="date"
              data-testid="date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-transparent border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300"
            />
            <span className="text-slate-600 text-xs">→</span>
            <input
              type="date"
              data-testid="date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-transparent border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300"
            />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="notification-bell" className="relative p-2 text-slate-400 hover:text-white">
                <Bell className="h-5 w-5" />
                {overdue.length > 0 && (
                  <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-[#DC2626] text-[9px] text-white flex items-center justify-center">
                    {overdue.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 bg-[#1E293B] border-slate-700" align="end">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Overdue Coaching</p>
              {overdue.length === 0 ? (
                <p className="text-sm text-slate-400">No overdue sessions. All caught up.</p>
              ) : (
                <ul className="space-y-1.5">
                  {overdue.map((m, i) => (
                    <li key={i} className="text-sm text-slate-300 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#DC2626]" />{m}
                    </li>
                  ))}
                </ul>
              )}
            </PopoverContent>
          </Popover>
        </header>

        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
